import { useEffect, useRef, useSyncExternalStore, type ReactNode } from 'react';
import { toast } from './ToastHost';







type ModalState = {
  id: number;
  title: string;
  body: ReactNode;
  foot: ReactNode;
  wide: boolean;
};

let current: ModalState | null = null;
let activeClose: (() => void) | null = null;
let seq = 0;
const listeners = new Set<() => void>();

function emit(): void {
  for (const l of listeners) l();
}

function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

function getSnapshot(): ModalState | null {
  return current;
}

export function closeModal(): void {
  activeClose?.();
}

export function modal(title: string, body: ReactNode, foot?: ReactNode, wide?: boolean): () => void {
  closeModal();
  const id = ++seq;
  current = { id, title, body, foot: foot ?? null, wide: Boolean(wide) };
  const close = () => {
    if (activeClose !== close) return;
    activeClose = null;
    current = null;
    emit();
  };
  activeClose = close;
  emit();
  return close;
}

export function confirmDialog(
  title: string,
  text: ReactNode,
  onYes: () => void,
  o: { word?: string; yes?: string } = {},
): void {
  const typed = { value: '' };
  const close = modal(
    title,
    <>
      <div className="note" style={{ borderLeftColor: 'var(--err)' }}>
        {text}
      </div>
      {o.word ? (
        <div className="field-row">
          <label htmlFor="cfW">
            Введите <code>{o.word}</code> для подтверждения
          </label>
          <input
            type="text"
            id="cfW"
            spellCheck={false}
            autoComplete="off"
            onChange={(e) => {
              typed.value = e.target.value;
            }}
          />
        </div>
      ) : null}
    </>,
    <>
      <button type="button" className="btn" data-close>
        Отмена
      </button>
      <button
        type="button"
        className="btn btn-danger"
        id="cfYes"
        onClick={() => {
          if (o.word && typed.value.trim() !== o.word) {
            toast(`Слово введено неверно. Введите «${o.word}» точно как указано, чтобы подтвердить действие.`, 'err');
            return;
          }
          close();
          onYes();
        }}
      >
        {o.yes || 'Удалить'}
      </button>
    </>,
  );
}

function ModalView({ m }: { m: ModalState }) {
  const bodyRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onEsc = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') closeModal();
    };
    document.addEventListener('keydown', onEsc);
    bodyRef.current?.querySelector<HTMLElement>('input,select,textarea')?.focus();
    return () => document.removeEventListener('keydown', onEsc);
  }, [m.id]);

  return (
    <div
      className="overlay"
      onClick={(ev) => {
        const t = ev.target;
        if (!(t instanceof Element)) return;
        if (t === ev.currentTarget || t.closest('[data-close]')) closeModal();
      }}
    >
      <div className={'modal' + (m.wide ? ' wide' : '')} role="dialog" aria-modal="true" aria-label={m.title}>
        <div className="modal-head">
          <h2>{m.title}</h2>
          <button className="icon-btn" type="button" data-close style={{ marginLeft: 'auto' }} aria-label="Закрыть">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
              <path d="M6 6l12 12M18 6 6 18" />
            </svg>
          </button>
        </div>
        <div className="modal-body" ref={bodyRef}>
          {m.body}
        </div>
        <div className="modal-foot">{m.foot}</div>
      </div>
    </div>
  );
}

export function ModalHost() {
  const m = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  return <div id="modals">{m ? <ModalView key={m.id} m={m} /> : null}</div>;
}
